import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

/**
 * AI model registry and version control (part 80).
 *
 * A version is an exact artefact identified by its SHA-256, not a mutable label. The page makes
 * the promotion gate visible rather than hiding it behind a disabled button: the latest
 * evaluation and its threshold are shown next to every version, and the approval/promotion
 * actions explain why they are unavailable when the evidence does not permit activation.
 *
 * Rollback is offered only for the version the current production row actually displaced; the
 * API refuses anything else, so the UI never invents a "previous" version from sort order.
 */

type Stage = 'draft' | 'evaluating' | 'approved' | 'production' | 'retired'
type Verdict = 'pass' | 'fail'

interface RegistryEvaluation {
  id: string
  model_id: string
  version_id: string
  dataset: string
  sample_count: number
  score_bps: number
  threshold_bps: number
  verdict: Verdict
  notes?: string
  evaluated_by?: string
  evaluated_at: string
}

interface RegistryVersion {
  id: string
  model_id: string
  version: string
  stage: Stage
  stage_note?: string
  artefact_name: string
  artefact_uri?: string
  artefact_sha256: string
  changelog?: string
  created_by?: string
  created_at: string
  updated_at: string
  submitted_at?: string
  approved_at?: string
  approved_by?: string
  activated_at?: string
  retired_at?: string
  previous_production_version_id?: string
  evaluation_count: number
  latest_evaluation?: RegistryEvaluation
}

interface RegistryModel {
  id: string
  key: string
  name: string
  description?: string
  task: string
  owner_role: string
  created_by?: string
  created_at: string
  updated_at: string
  version_count: number
  production_version_id?: string
  production_version?: string
  versions?: RegistryVersion[]
}

const MODELS_QUERY = ['registry-models'] as const
const modelQuery = (id: string) => ['registry-model', id] as const

const stageLabel: Record<Stage, string> = {
  draft: 'ฉบับร่าง',
  evaluating: 'กำลังประเมิน',
  approved: 'อนุมัติแล้ว',
  production: 'ใช้งานจริง',
  retired: 'ปลดระวาง',
}

const stageTone: Record<Stage, string> = {
  draft: 'dim',
  evaluating: 'sev-medium',
  approved: 'sev-low',
  production: 'sev-low',
  retired: 'dim',
}

const taskLabel: Record<string, string> = {
  classification: 'จำแนกประเภท',
  extraction: 'สกัดข้อมูล',
  summarisation: 'สรุปความ',
  risk_scoring: 'ให้คะแนนความเสี่ยง',
  anomaly_detection: 'ตรวจจับความผิดปกติ',
  other: 'อื่น ๆ',
}

const roleLabel: Record<string, string> = {
  owner: 'เจ้าขององค์กร',
  admin: 'ผู้ดูแลระบบ',
  audit_manager: 'ผู้จัดการตรวจสอบ',
  auditor: 'ผู้ตรวจสอบ',
  reviewer: 'ผู้ทบทวน',
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function pct(bps: number): string {
  return `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 1)}%`
}

function shortSha(sha: string): string {
  return sha.length > 16 ? `${sha.slice(0, 10)}…${sha.slice(-6)}` : sha
}

function evalPasses(ev?: RegistryEvaluation): boolean {
  return !!ev && ev.verdict === 'pass' && ev.score_bps >= ev.threshold_bps
}

function canPromote(v: RegistryVersion): boolean {
  return v.stage === 'approved' && evalPasses(v.latest_evaluation)
}

export function ModelRegistry() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const canWork = ['owner', 'admin', 'audit_manager'].includes(me?.role ?? '')
  const canActivate = ['owner', 'admin'].includes(me?.role ?? '')

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [showCreate, setShowCreate] = useState(false)
  const [modelKey, setModelKey] = useState('')
  const [modelName, setModelName] = useState('')
  const [modelTask, setModelTask] = useState('risk_scoring')
  const [modelOwner, setModelOwner] = useState('audit_manager')
  const [modelDescription, setModelDescription] = useState('')

  const [versionNo, setVersionNo] = useState('')
  const [artefactName, setArtefactName] = useState('')
  const [artefactURI, setArtefactURI] = useState('')
  const [artefactSha, setArtefactSha] = useState('')
  const [changelog, setChangelog] = useState('')

  const [evalVersionId, setEvalVersionId] = useState<string | null>(null)
  const [datasetId, setDatasetId] = useState('golden-set')
  const [sampleCount, setSampleCount] = useState(1000)
  const [scorePct, setScorePct] = useState(90)
  const [thresholdPct, setThresholdPct] = useState(80)
  const [verdict, setVerdict] = useState<Verdict>('pass')
  const [evalNotes, setEvalNotes] = useState('')

  const models = useQuery({
    queryKey: MODELS_QUERY,
    queryFn: () => api<RegistryModel[]>('/v1/registry/models'),
    staleTime: 10_000,
  })

  useEffect(() => {
    if (!selectedId && models.data && models.data.length > 0) setSelectedId(models.data[0].id)
  }, [models.data, selectedId])

  const detail = useQuery({
    queryKey: modelQuery(selectedId ?? 'none'),
    queryFn: async () => {
      const body = await api<{ model: RegistryModel }>(`/v1/registry/models/${selectedId}`)
      return body.model
    },
    enabled: selectedId !== null,
  })

  const evaluations = useQuery({
    queryKey: ['registry-evaluations', selectedId ?? 'none', evalVersionId ?? 'none'],
    queryFn: () => api<RegistryEvaluation[]>(
      `/v1/registry/models/${selectedId}/versions/${evalVersionId}/evaluations`,
    ),
    enabled: selectedId !== null && evalVersionId !== null,
    staleTime: 5_000,
  })

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: MODELS_QUERY })
    if (selectedId) void qc.invalidateQueries({ queryKey: modelQuery(selectedId) })
    void qc.invalidateQueries({ queryKey: ['registry-evaluations'] })
  }

  const createModel = useMutation({
    mutationFn: () => apiWrite<{ model: RegistryModel }>('/v1/registry/models', 'POST', {
      key: modelKey,
      name: modelName,
      description: modelDescription,
      task: modelTask,
      owner_role: modelOwner,
    }),
    onSuccess: (body) => {
      setNotice('ลงทะเบียนโมเดลแล้ว ยังไม่มีเวอร์ชันใดเปิดใช้งาน')
      setSelectedId(body.model.id)
      setEvalVersionId(null)
      setModelKey('')
      setModelName('')
      setModelDescription('')
      setShowCreate(false)
      refresh()
    },
  })

  const registerVersion = useMutation({
    mutationFn: () => apiWrite<{ model: RegistryModel }>(
      `/v1/registry/models/${selectedId}/versions`, 'POST',
      {
        version: versionNo,
        artefact_name: artefactName,
        artefact_uri: artefactURI,
        artefact_sha256: artefactSha,
        changelog,
      },
    ),
    onSuccess: () => {
      setNotice('ลงทะเบียนเวอร์ชันเป็นฉบับร่างแล้ว ต้องส่งประเมินก่อนจึงจะอนุมัติได้')
      setVersionNo('')
      setArtefactName('')
      setArtefactURI('')
      setArtefactSha('')
      setChangelog('')
      refresh()
    },
  })

  const submitVersion = useMutation({
    mutationFn: (versionID: string) => apiWrite<{ model: RegistryModel }>(
      `/v1/registry/models/${selectedId}/versions/${versionID}/submit`, 'POST', { note: 'ส่งประเมิน' },
    ),
    onSuccess: () => {
      setNotice('ส่งเวอร์ชันเข้าสู่การประเมินแล้ว')
      refresh()
    },
  })

  const recordEvaluation = useMutation({
    mutationFn: (input: { versionID: string }) => apiWrite<{ model: RegistryModel }>(
      `/v1/registry/models/${selectedId}/versions/${input.versionID}/evaluations`, 'POST',
      {
        dataset: datasetId,
        sample_count: sampleCount,
        score_bps: Math.round(scorePct * 100),
        threshold_bps: Math.round(thresholdPct * 100),
        verdict,
        notes: evalNotes,
      },
    ),
    onSuccess: (body, input) => {
      const version = body.model.versions?.find((v) => v.id === input.versionID)
      const passed = evalPasses(version?.latest_evaluation)
      setNotice(passed
        ? 'บันทึกผลประเมินที่ผ่านแล้ว เวอร์ชันพร้อมให้อนุมัติ'
        : 'บันทึกผลประเมินแล้ว แต่ยังไม่ผ่านเกณฑ์ จึงยังอนุมัติและเปิดใช้งานไม่ได้')
      setEvalVersionId(null)
      setEvalNotes('')
      refresh()
    },
  })

  const approveVersion = useMutation({
    mutationFn: (versionID: string) => apiWrite<{ model: RegistryModel }>(
      `/v1/registry/models/${selectedId}/versions/${versionID}/approve`, 'POST', { note: 'อนุมัติจากหน้าทะเบียน' },
    ),
    onSuccess: () => {
      setNotice('อนุมัติเวอร์ชันแล้ว ยังต้องเปิดใช้งานเป็นขั้นตอนแยก')
      refresh()
    },
  })

  const promoteVersion = useMutation({
    mutationFn: (versionID: string) => apiWrite<{ model: RegistryModel }>(
      `/v1/registry/models/${selectedId}/versions/${versionID}/promote`, 'POST', { note: 'เปิดใช้งานจริง' },
    ),
    onSuccess: () => {
      setNotice('เปิดใช้งานเวอร์ชันนี้แล้ว เวอร์ชันที่เคยใช้งานถูกพักเป็นสถานะอนุมัติเพื่อย้อนกลับได้')
      refresh()
    },
  })

  const rollback = useMutation({
    mutationFn: () => apiWrite<{ model: RegistryModel }>(
      `/v1/registry/models/${selectedId}/rollback`, 'POST', { note: 'ย้อนกลับเวอร์ชันที่ใช้งานจริง' },
    ),
    onSuccess: () => {
      setNotice('ย้อนกลับไปยังเวอร์ชันที่ใช้งานก่อนหน้าแล้ว')
      refresh()
    },
  })

  const retireVersion = useMutation({
    mutationFn: (versionID: string) => apiWrite<{ model: RegistryModel }>(
      `/v1/registry/models/${selectedId}/versions/${versionID}/retire`, 'POST', { note: 'ปลดระวาง' },
    ),
    onSuccess: () => {
      setNotice('ปลดระวางเวอร์ชันแล้ว')
      refresh()
    },
  })

  const current = detail.data
  const rows = models.data ?? []
  const production = useMemo(
    () => current?.versions?.find((v) => v.stage === 'production'),
    [current],
  )
  const rollbackTarget = useMemo(() => {
    if (!current || !production?.previous_production_version_id) return undefined
    return current.versions?.find(
      (v) => v.id === production.previous_production_version_id && v.stage === 'approved',
    )
  }, [current, production])

  const counts = useMemo(() => ({
    models: rows.length,
    versions: rows.reduce((sum, m) => sum + m.version_count, 0),
    live: rows.filter((m) => !!m.production_version_id).length,
    idle: rows.filter((m) => !m.production_version_id).length,
  }), [rows])

  const mutationError =
    createModel.error ?? registerVersion.error ?? submitVersion.error ?? recordEvaluation.error ??
    approveVersion.error ?? promoteVersion.error ?? rollback.error ?? retireVersion.error

  const selectVersionForEvaluation = (v: RegistryVersion) => {
    setEvalVersionId(v.id)
    setDatasetId(v.latest_evaluation?.dataset ?? 'golden-set')
    setSampleCount(v.latest_evaluation?.sample_count ?? 1000)
    setScorePct(90)
    setThresholdPct(v.latest_evaluation ? v.latest_evaluation.threshold_bps / 100 : 80)
    setVerdict('pass')
    setEvalNotes('')
  }

  return (
    <div className="page">
      <PageHead
        title="ทะเบียนโมเดล AI"
        sub="ควบคุมเวอร์ชันของโมเดลที่ใช้งานจริง ตรวจ artefact ด้วย SHA-256 และเปิดใช้งานได้เฉพาะรุ่นที่ผลประเมินล่าสุดผ่านเกณฑ์"
        actions={<span className="chip gold">เปิดใช้งานเมื่อประเมินผ่านเท่านั้น</span>}
      />

      {mutationError ? (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {errorText(mutationError, 'ดำเนินการไม่สำเร็จ')}
        </div>
      ) : null}
      {notice ? (
        <div className="card card-pad rise" style={{ marginBottom: 16, borderColor: 'rgba(200,169,106,0.35)' }}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12.5 }}>{notice}</span>
            <button className="btn btn-sm btn-ghost" onClick={() => setNotice(null)}>ปิดข้อความ</button>
          </div>
        </div>
      ) : null}

      <div className="grid-4" style={{ marginBottom: 16 }}>
        <Stat label="โมเดลในทะเบียน" value={counts.models} foot="ตัวตนของโมเดลที่องค์กรลงทะเบียน" />
        <Stat label="เวอร์ชันทั้งหมด" value={counts.versions} foot="นับรวมทุกสถานะ รวมรุ่นที่ปลดระวาง" />
        <Stat label="กำลังใช้งานจริง" value={counts.live} foot="โมเดลที่มีเวอร์ชัน production อยู่" />
        <Stat label="ยังไม่มีรุ่นใช้งาน" value={counts.idle} foot="โมเดลที่ยังไม่ผ่านการเปิดใช้งาน" />
      </div>

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span className="card-label" style={{ margin: 0 }}>ลงทะเบียนโมเดลใหม่</span>
          <button className="btn btn-sm" onClick={() => setShowCreate((v) => !v)} disabled={!canWork}>
            {showCreate ? 'ยกเลิก' : 'เพิ่มโมเดล'}
          </button>
        </div>
        {showCreate ? (
          <div style={{ marginTop: 12 }}>
            <div className="grid-3">
              <div className="field">
                <label htmlFor="registry-key">คีย์โมเดล</label>
                <input id="registry-key" className="input" value={modelKey}
                  placeholder="risk_scorer" onChange={(e) => setModelKey(e.target.value)} />
                <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>ตัวพิมพ์เล็กและขีดล่าง ใช้ระบุตัวโมเดลถาวร</div>
              </div>
              <div className="field">
                <label htmlFor="registry-name">ชื่อที่แสดง</label>
                <input id="registry-name" className="input" value={modelName}
                  placeholder="ตัวให้คะแนนความเสี่ยง" onChange={(e) => setModelName(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="registry-task">งานของโมเดล</label>
                <select id="registry-task" className="select" value={modelTask}
                  onChange={(e) => setModelTask(e.target.value)}>
                  {Object.entries(taskLabel).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid-2" style={{ marginTop: 10 }}>
              <div className="field">
                <label htmlFor="registry-owner">บทบาทเจ้าของโมเดล</label>
                <select id="registry-owner" className="select" value={modelOwner}
                  onChange={(e) => setModelOwner(e.target.value)}>
                  {['owner', 'admin', 'audit_manager', 'auditor', 'reviewer'].map((role) => (
                    <option key={role} value={role}>{roleLabel[role]}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="registry-description">คำอธิบาย</label>
                <input id="registry-description" className="input" value={modelDescription}
                  maxLength={2000} onChange={(e) => setModelDescription(e.target.value)} />
              </div>
            </div>
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn btn-primary" disabled={!canWork || !modelKey || !modelName || createModel.isPending}
                onClick={() => createModel.mutate()}>
                {createModel.isPending ? 'กำลังบันทึก…' : 'ลงทะเบียนโมเดล'}
              </button>
              {!canWork ? <span className="dim" style={{ fontSize: 11.5 }}>บทบาทของคุณสร้างโมเดลไม่ได้</span> : null}
            </div>
          </div>
        ) : (
          <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
            โมเดลหนึ่งตัวมีได้หลายเวอร์ชัน แต่มีเวอร์ชันที่ใช้งานจริงได้เพียงหนึ่งรุ่นในเวลาเดียวกัน
          </div>
        )}
      </div>

      <div className="grid-main">
        <div>
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="card-label" style={{ margin: 0 }}>โมเดลทั้งหมด</span>
            <span className="chip" style={{ marginLeft: 'auto' }}>{rows.length} โมเดล</span>
          </div>
          {models.isLoading ? <Skel h={260} /> : rows.length === 0 ? (
            <Empty title="ยังไม่มีโมเดลในทะเบียน" body="เพิ่มโมเดลก่อน แล้วจึงลงทะเบียนเวอร์ชันและบันทึกผลประเมินเพื่อขอเปิดใช้งาน" />
          ) : (
            <div className="table-wrap">
              <table className="table data">
                <thead>
                  <tr>
                    <th>โมเดล</th>
                    <th>งาน</th>
                    <th>เวอร์ชัน</th>
                    <th>ใช้งานจริง</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => (
                    <tr key={m.id} onClick={() => { setSelectedId(m.id); setEvalVersionId(null) }}
                      style={{ cursor: 'pointer', background: m.id === selectedId ? 'rgba(200,169,106,0.06)' : undefined }}>
                      <td>
                        <div>{m.name}</div>
                        <div className="mono dim" style={{ fontSize: 10.5 }}>{m.key}</div>
                      </td>
                      <td className="dim" style={{ fontSize: 11.5 }}>{taskLabel[m.task] ?? m.task}</td>
                      <td className="tnum">{m.version_count}</td>
                      <td>
                        {m.production_version
                          ? <span className="chip gold">{m.production_version}</span>
                          : <span className="dim" style={{ fontSize: 11 }}>ยังไม่มี</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div>
          {!selectedId ? (
            <div className="card card-pad"><span className="dim" style={{ fontSize: 12 }}>เลือกโมเดลเพื่อดูเวอร์ชัน</span></div>
          ) : detail.isLoading ? <Skel h={320} /> : !current ? (
            <Empty title="ไม่พบโมเดล" body="โมเดลนี้อาจถูกลบหรือคุณไม่มีสิทธิ์เข้าถึง" />
          ) : (
            <>
              <div className="card card-pad" style={{ marginBottom: 16 }}>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div className="heading-m">{current.name}</div>
                    <div className="mono dim" style={{ fontSize: 11, marginTop: 3 }}>{current.key}</div>
                    {current.description ? (
                      <div className="dim" style={{ fontSize: 11.5, marginTop: 6, lineHeight: 1.7 }}>{current.description}</div>
                    ) : null}
                  </div>
                  <span className="chip">{taskLabel[current.task] ?? current.task}</span>
                </div>
                <div className="row" style={{ marginTop: 12, gap: 8, flexWrap: 'wrap' }}>
                  <span className="dim" style={{ fontSize: 11 }}>เจ้าของ {roleLabel[current.owner_role] ?? current.owner_role}</span>
                  <span className="dim" style={{ fontSize: 11 }}>เวอร์ชัน {current.version_count}</span>
                  <span className="dim" style={{ fontSize: 11 }}>
                    ใช้งานจริง {current.production_version ?? 'ยังไม่มี'}
                  </span>
                </div>
                {production && rollbackTarget ? (
                  <div className="row" style={{ marginTop: 12, gap: 10, flexWrap: 'wrap' }}>
                    <button className="btn btn-sm" disabled={!canActivate || rollback.isPending}
                      onClick={() => rollback.mutate()}>
                      {rollback.isPending ? 'กำลังย้อนกลับ…' : `ย้อนกลับไปยัง ${rollbackTarget.version}`}
                    </button>
                    <span className="dim" style={{ fontSize: 10.5, alignSelf: 'center' }}>
                      คืนค่าจาก {production.version} ไปยังรุ่นที่ใช้งานก่อนหน้า
                    </span>
                  </div>
                ) : null}
                {!canActivate ? (
                  <div className="dim" style={{ fontSize: 10.5, marginTop: 8 }}>
                    การเปิดใช้งานและย้อนกลับทำได้โดยเจ้าขององค์กรหรือผู้ดูแลระบบเท่านั้น
                  </div>
                ) : null}
              </div>

              <div className="card card-pad" style={{ marginBottom: 16 }}>
                <div className="card-label">ลงทะเบียนเวอร์ชันใหม่</div>
                <p className="dim" style={{ fontSize: 11, lineHeight: 1.7, marginTop: 6 }}>
                  ระบบเก็บเฉพาะ metadata และค่า SHA-256 ของไฟล์ ไม่ได้อัปโหลดไฟล์จริง
                  เมื่อเวอร์ชันออกจากฉบับร่างแล้ว ชื่อไฟล์ ที่อยู่ และค่าแฮชจะแก้ไขไม่ได้
                </p>
                <div className="grid-3" style={{ marginTop: 10 }}>
                  <div className="field">
                    <label htmlFor="registry-version">เวอร์ชัน (semver)</label>
                    <input id="registry-version" className="input" value={versionNo}
                      placeholder="1.0.0" onChange={(e) => setVersionNo(e.target.value)} />
                  </div>
                  <div className="field">
                    <label htmlFor="registry-artefact">ชื่อไฟล์ artefact</label>
                    <input id="registry-artefact" className="input" value={artefactName}
                      placeholder="scorer-1.0.0.onnx" maxLength={255} onChange={(e) => setArtefactName(e.target.value)} />
                  </div>
                  <div className="field">
                    <label htmlFor="registry-uri">ที่อยู่ artefact</label>
                    <input id="registry-uri" className="input" value={artefactURI}
                      placeholder="s3://auditai-models/…" maxLength={1000} onChange={(e) => setArtefactURI(e.target.value)} />
                  </div>
                </div>
                <div className="field" style={{ marginTop: 8 }}>
                  <label htmlFor="registry-sha">SHA-256 ของไฟล์</label>
                  <input id="registry-sha" className="input mono" value={artefactSha} maxLength={64}
                    placeholder="64 ตัวอักษรฐานสิบหกตัวพิมพ์เล็ก" onChange={(e) => setArtefactSha(e.target.value)} />
                </div>
                <div className="field" style={{ marginTop: 8 }}>
                  <label htmlFor="registry-changelog">บันทึกการเปลี่ยนแปลง</label>
                  <textarea id="registry-changelog" className="input" rows={2} maxLength={4000}
                    value={changelog} onChange={(e) => setChangelog(e.target.value)} />
                </div>
                <div className="row" style={{ marginTop: 10 }}>
                  <button className="btn btn-primary" disabled={
                    !canWork || !versionNo || !artefactName || artefactSha.length !== 64 || registerVersion.isPending
                  } onClick={() => registerVersion.mutate()}>
                    {registerVersion.isPending ? 'กำลังบันทึก…' : 'ลงทะเบียนเวอร์ชัน'}
                  </button>
                </div>
              </div>

              <div className="card card-pad">
                <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
                  <span className="card-label" style={{ margin: 0 }}>เวอร์ชันทั้งหมด</span>
                  <span className="chip">{(current.versions ?? []).length} เวอร์ชัน</span>
                </div>

                {(current.versions ?? []).length === 0 ? (
                  <span className="dim" style={{ fontSize: 11.5 }}>ยังไม่มีเวอร์ชัน ลงทะเบียนรุ่นแรกด้านบน</span>
                ) : (
                  (current.versions ?? []).map((v) => {
                    const latest = v.latest_evaluation
                    const passing = evalPasses(latest)
                    const evaluating = evalVersionId === v.id
                    const gateHint = v.stage === 'approved' && !passing
                      ? 'อนุมัติแล้ว แต่ผลประเมินล่าสุดไม่ผ่านหรือไม่มีผล จึงยังเปิดใช้งานไม่ได้'
                      : v.stage === 'evaluating' && !latest
                        ? 'ยังไม่มีผลประเมิน ต้องบันทึกผลก่อนขออนุมัติ'
                        : ''
                    return (
                      <div key={v.id} style={{ padding: '14px 0', borderTop: '1px solid var(--color-hairline)' }}>
                        <div className="row" style={{ alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                          <span className="chip gold" style={{ minWidth: 62, justifyContent: 'center' }}>{v.version}</span>
                          <div style={{ flex: 1, minWidth: 220 }}>
                            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                              {v.stage === 'production' ? (
                                <span className="chip gold" style={{ fontSize: 10.5 }}>ใช้งานจริง</span>
                              ) : (
                                <span className={`sev ${stageTone[v.stage]}`} style={{ fontSize: 10.5 }}>
                                  <i className="sev-dot" />{stageLabel[v.stage]}
                                </span>
                              )}
                              <span className="dim" style={{ fontSize: 10.5 }}>{v.artefact_name}</span>
                              <span className="mono dim" style={{ fontSize: 10 }} title={v.artefact_sha256}>
                                {shortSha(v.artefact_sha256)}
                              </span>
                            </div>
                            <div className="row" style={{ marginTop: 6, gap: 10, flexWrap: 'wrap' }}>
                              {latest ? (
                                <span className={`sev ${passing ? 'sev-low' : 'sev-critical'}`} style={{ fontSize: 10.5 }}>
                                  <i className="sev-dot" />
                                  ผลล่าสุด {latest.verdict === 'pass' ? 'ผ่าน' : 'ไม่ผ่าน'} · {pct(latest.score_bps)} / เกณฑ์ {pct(latest.threshold_bps)}
                                </span>
                              ) : (
                                <span className="sev sev-medium" style={{ fontSize: 10.5 }}>
                                  <i className="sev-dot" />ยังไม่มีผลประเมิน
                                </span>
                              )}
                              <span className="dim" style={{ fontSize: 10 }}>ผู้ประเมิน {v.evaluation_count} ครั้ง</span>
                              {v.activated_at ? (
                                <span className="dim" style={{ fontSize: 10 }}>เปิดใช้เมื่อ {thaiDateTime(v.activated_at)}</span>
                              ) : null}
                            </div>
                            {v.changelog ? (
                              <div className="dim" style={{ fontSize: 10.5, marginTop: 5, lineHeight: 1.7 }}>{v.changelog}</div>
                            ) : null}
                            {gateHint ? (
                              <div style={{ fontSize: 10.5, marginTop: 5, color: 'var(--color-critical)', lineHeight: 1.7 }}>
                                {gateHint}
                              </div>
                            ) : null}
                          </div>
                          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                            {canWork && v.stage === 'draft' ? (
                              <button className="btn btn-sm" disabled={submitVersion.isPending}
                                onClick={() => submitVersion.mutate(v.id)}>ส่งประเมิน</button>
                            ) : null}
                            {canWork && (v.stage === 'evaluating' || v.stage === 'approved') ? (
                              <button className="btn btn-sm" onClick={() => selectVersionForEvaluation(v)}>
                                {evaluating ? 'ปิดแบบฟอร์ม' : 'บันทึกผลประเมิน'}
                              </button>
                            ) : null}
                            {canWork && v.stage === 'evaluating' ? (
                              <button className="btn btn-sm btn-primary" disabled={!passing || approveVersion.isPending}
                                title={passing ? 'อนุมัติเวอร์ชัน' : 'ต้องมีผลประเมินล่าสุดที่ผ่านก่อน'}
                                onClick={() => approveVersion.mutate(v.id)}>อนุมัติ</button>
                            ) : null}
                            {canActivate && v.stage === 'approved' ? (
                              <button className="btn btn-sm btn-primary" disabled={!canPromote(v) || promoteVersion.isPending}
                                title={canPromote(v) ? 'เปิดใช้งานเวอร์ชันนี้' : 'ต้องมีผลประเมินล่าสุดที่ผ่านก่อน'}
                                onClick={() => promoteVersion.mutate(v.id)}>เปิดใช้งาน</button>
                            ) : null}
                            {canActivate && (v.stage === 'approved' || v.stage === 'production') ? (
                              <button className="btn btn-sm btn-ghost" disabled={retireVersion.isPending}
                                onClick={() => retireVersion.mutate(v.id)}>ปลดระวาง</button>
                            ) : null}
                          </div>
                        </div>

                        {evaluating ? (
                          <div style={{
                            marginTop: 12, padding: 12, border: '1px solid var(--color-hairline)',
                            borderRadius: 8, background: 'rgba(255,255,255,0.015)',
                          }}>
                            <div className="card-label" style={{ marginTop: 0 }}>บันทึกผลประเมินสำหรับ {v.version}</div>
                            <div className="grid-3" style={{ marginTop: 8 }}>
                              <div className="field">
                                <label htmlFor="registry-dataset">ชุดข้อมูลทดสอบ</label>
                                <input id="registry-dataset" className="input" value={datasetId}
                                  onChange={(e) => setDatasetId(e.target.value)} />
                              </div>
                              <div className="field">
                                <label htmlFor="registry-samples">จำนวนตัวอย่าง</label>
                                <input id="registry-samples" className="input tnum" type="number" min={1}
                                  value={sampleCount} onChange={(e) => setSampleCount(Number(e.target.value))} />
                              </div>
                              <div className="field">
                                <label htmlFor="registry-verdict">ผลตัดสิน</label>
                                <select id="registry-verdict" className="select" value={verdict}
                                  onChange={(e) => setVerdict(e.target.value as Verdict)}>
                                  <option value="pass">ผ่าน</option>
                                  <option value="fail">ไม่ผ่าน</option>
                                </select>
                              </div>
                            </div>
                            <div className="grid-2" style={{ marginTop: 8 }}>
                              <div className="field">
                                <label htmlFor="registry-score">คะแนน (เปอร์เซ็นต์)</label>
                                <input id="registry-score" className="input tnum" type="number" min={0} max={100} step={0.1}
                                  value={scorePct} onChange={(e) => setScorePct(Number(e.target.value))} />
                              </div>
                              <div className="field">
                                <label htmlFor="registry-threshold">เกณฑ์ผ่าน (เปอร์เซ็นต์)</label>
                                <input id="registry-threshold" className="input tnum" type="number" min={1} max={100} step={0.1}
                                  value={thresholdPct} onChange={(e) => setThresholdPct(Number(e.target.value))} />
                              </div>
                            </div>
                            <div className="field" style={{ marginTop: 8 }}>
                              <label htmlFor="registry-eval-notes">บันทึกผลประเมิน</label>
                              <textarea id="registry-eval-notes" className="input" rows={2} maxLength={4000}
                                value={evalNotes} onChange={(e) => setEvalNotes(e.target.value)} />
                            </div>
                            <div className="row" style={{ marginTop: 10, gap: 10, flexWrap: 'wrap' }}>
                              <button className="btn btn-primary" disabled={
                                !canWork || !datasetId || sampleCount < 1 || recordEvaluation.isPending ||
                                (verdict === 'pass' && scorePct < thresholdPct)
                              } onClick={() => recordEvaluation.mutate({ versionID: v.id })}>
                                {recordEvaluation.isPending ? 'กำลังบันทึก…' : 'บันทึกผลประเมิน'}
                              </button>
                              <button className="btn btn-sm btn-ghost" onClick={() => setEvalVersionId(null)}>ยกเลิก</button>
                              {verdict === 'pass' && scorePct < thresholdPct ? (
                                <span className="dim" style={{ fontSize: 10.5, color: 'var(--color-critical)', alignSelf: 'center' }}>
                                  ผลผ่านต้องมีคะแนนไม่ต่ำกว่าเกณฑ์
                                </span>
                              ) : null}
                            </div>

                            {evaluations.data && evaluations.data.length > 0 ? (
                              <div style={{ marginTop: 12 }}>
                                <div className="card-label" style={{ marginTop: 0 }}>ประวัติผลประเมิน</div>
                                {evaluations.data.map((e) => (
                                  <div key={e.id} className="evidence-row">
                                    <span className="dim">·</span>
                                    <span className={`sev ${evalPasses(e) ? 'sev-low' : 'sev-critical'}`} style={{ fontSize: 10 }}>
                                      {e.verdict === 'pass' ? 'ผ่าน' : 'ไม่ผ่าน'} {pct(e.score_bps)}
                                    </span>
                                    <span className="dim" style={{ fontSize: 10.5 }}>
                                      {e.dataset} · {e.sample_count} ตัวอย่าง
                                    </span>
                                    <span className="dim tnum" style={{ fontSize: 10, marginLeft: 'auto' }}>
                                      {thaiDateTime(e.evaluated_at)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    )
                  })
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
